// Live price chart playback (tick mode): pure functions for the frame loop of the next slice. The page plays the
// 1 s points back at a fixed delay behind the server clock, so the head moves at a constant speed between polls.
// Nothing here requests data, polls, starts a timer or reads a clock (ADR 094): the caller passes the time in.
import type { TickPoint } from './model';

/**
 * The default playback delay: the head shows the price of serverNow - 6 s. The page polls ticks-tail every 2 s, and the
 * newest point is 1.5-3.5 s old when the server sends it (measured 10/10), so a shorter delay stalls the head. The S4
 * price push may pass 3 s (founder decision); callers pass the delay to advancePlayback and do not change this value.
 */
export const PLAYBACK_DELAY_S = 6;
/** The highest playback speed while the head catches up after a stall. */
export const MAX_CATCH_UP_SPEED = 1.5;
/** A head further behind than this jumps to the target instead of catching up. */
export const JUMP_IF_BEHIND_S = 10;
/** The default view of an open round: the last 90 seconds. */
export const LIVE_WINDOW_S = 90;
/** The live view in bars (one bar per second). */
export const liveWindowBars = LIVE_WINDOW_S;
// Within this lag the head plays at real time; the speed then rises linearly to the maximum at FULL_SPEED_LAG_S.
const ON_TARGET_S = 0.25;
const FULL_SPEED_LAG_S = 2;

export type PlaybackState = { shownAt: number };
export type PlaybackFrame = { shownAt: number; stalled: boolean };
export type PriceRange = { min: number; max: number };

const finite = (value: number | undefined): value is number => Number.isFinite(value);
/** The delay the playback runs at: delayS, or PLAYBACK_DELAY_S for a delayS that is missing, negative or not finite. */
export const playbackDelay = (delayS?: number) => (finite(delayS) && delayS >= 0 ? delayS : PLAYBACK_DELAY_S);

/**
 * The second the chart shows on this frame. The target is serverNow - delayS (default PLAYBACK_DELAY_S, also for a
 * delayS that is negative or not finite).
 * - The head never passes the last point (no extrapolation). `stalled`: the head holds on the last point and the
 *   target is past it. Without data the head is stalled, and with no state it is -Infinity (nothing to show).
 * - No state, or more than JUMP_IF_BEHIND_S behind: jump to min(target, last point). Otherwise advance dtS x speed:
 *   1 within 0.25 s of the target, up to MAX_CATCH_UP_SPEED when further behind, never past the target.
 * - The head never moves back. A state ahead of the data (a replaced history) starts a new playback; the caller also
 *   passes `undefined` on a new round. dtS <= 0 or a non-finite input keeps the state (or jumps without one).
 */
export const advancePlayback = (
  prev: PlaybackState | undefined,
  {
    serverNow,
    dtS,
    lastPointTime,
    delayS = PLAYBACK_DELAY_S,
  }: { serverNow: number; dtS: number; lastPointTime: number | undefined; delayS?: number }
): PlaybackFrame => {
  const target = serverNow - playbackDelay(delayS);
  const last = finite(lastPointTime) ? lastPointTime : undefined;
  const shown = (shownAt: number): PlaybackFrame => ({
    shownAt,
    stalled: last === undefined || (shownAt >= last && !(target <= last)),
  });
  // A state ahead of the data belongs to a replaced history: it starts a new playback.
  const from = finite(prev?.shownAt) && (last === undefined || prev.shownAt <= last) ? prev.shownAt : undefined;
  if (last === undefined) return shown(from ?? -Infinity);
  if (from === undefined) return shown(finite(target) ? Math.min(target, last) : last);
  if (!finite(target) || !finite(dtS) || dtS <= 0) return shown(from);
  const lag = target - from;
  if (lag > JUMP_IF_BEHIND_S) return shown(Math.min(target, last));
  // On or past the target (the server clock stepped back): hold, never move back.
  if (lag <= 0) return shown(from);
  const speed =
    1 + (MAX_CATCH_UP_SPEED - 1) * Math.min(1, Math.max(0, lag - ON_TARGET_S) / (FULL_SPEED_LAG_S - ON_TARGET_S));
  return shown(Math.min(from + dtS * speed, target, last));
};

/**
 * The price at second `t` (a fraction of a second between two points). `points`: ascending whole seconds, maybe
 * with gaps; a missing second takes the price of the second before it. `k` is the epoch second at or before `t`
 * and `frac` the part of the next second, so value = v(k) + frac x (v(k+1) - v(k)), exact at a whole second.
 * After the last point it holds the last price (k = last second, frac 0). Undefined before the first point.
 * Binary search: O(log n) reads.
 */
export const interpolateAt = (
  points: readonly TickPoint[],
  t: number
): { k: number; frac: number; value: number } | undefined => {
  if (!points.length || !(t >= points[0][0])) return undefined;
  const [lastTime, lastValue] = points[points.length - 1];
  if (t >= lastTime) return { k: lastTime, frac: 0, value: lastValue };
  const k = Math.floor(t);
  // The last point at or before second k: points[lo][0] <= k < points[hi][0].
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >>> 1;
    if (points[mid][0] <= k) lo = mid;
    else hi = mid;
  }
  const value = points[lo][1];
  const [nextTime, nextValue] = points[hi];
  const frac = t - k;
  return { k, frac, value: nextTime <= k + 1 ? value + frac * (nextValue - value) : value };
};

/**
 * The y range of the next frame. A side that must widen to hold the target jumps at once, so the head is never
 * clipped. A side that narrows eases in: prev + (target - prev) x (1 - exp(-dt / tau)). No previous range, or a
 * non-finite one: the target. A non-finite target: the previous range. dt <= 0 narrows nothing; tau <= 0 snaps.
 */
export const easeRange = (prev: PriceRange | undefined, target: PriceRange, dtMs: number, tauMs = 300): PriceRange => {
  const valid = (range: PriceRange | undefined): range is PriceRange => finite(range?.min) && finite(range?.max);
  if (!valid(target)) return valid(prev) ? prev : target;
  if (!valid(prev)) return target;
  const step = dtMs > 0 ? (tauMs > 0 ? 1 - Math.exp(-dtMs / tauMs) : 1) : 0;
  // Math.min/max hold the target: a side that must widen jumps to it at once, and rounding never leaves it outside.
  return {
    min: Math.min(target.min, prev.min + (target.min - prev.min) * step),
    max: Math.max(target.max, prev.max + (target.max - prev.max) * step),
  };
};

/**
 * The right offset in bars (timeScale().scrollToPosition) for the live view. The series ends on second k and the head
 * is `frac` bars after it, so an offset of headMarginBars + frac keeps the head headMarginBars from the right edge
 * while the chart scrolls. `frac` is clamped to [0, 1]; a non-finite one counts as 0.
 */
export const liveScrollPosition = (frac: number, headMarginBars = 2): number =>
  headMarginBars + (finite(frac) ? Math.min(1, Math.max(0, frac)) : 0);

/** A longer span is a bad feed: fillSeconds returns it unchanged. The chart window is 900 s. */
const MAX_FILL_SPAN_S = 3_600;

/**
 * One point per second from the first to the last point, so bar k of the series is second k. A missing second takes
 * the price of the second before it (as in interpolateAt). The given points are kept. Precondition: ascending whole
 * seconds. Other input (a repeated, falling, non-integer or non-finite time), or a span over MAX_FILL_SPAN_S, returns a
 * copy of the input. O(n + missing seconds).
 */
export const fillSeconds = (points: readonly TickPoint[]): TickPoint[] => {
  const filled: TickPoint[] = [];
  for (const point of points) {
    const prev = filled[filled.length - 1];
    // Checked before the gap is filled: a bad time costs O(1), and the fill never goes past the span cap.
    const ok = Number.isInteger(point[0]) && (!prev || (point[0] > prev[0] && point[0] - filled[0][0] <= MAX_FILL_SPAN_S));
    if (!ok) return [...points];
    if (prev) for (let time = prev[0] + 1; time < point[0]; time++) filled.push([time, prev[1]]);
    filled.push(point);
  }
  return filled;
};
